import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

// Modern, refined icons with better proportions
const IconDraft = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 7h16v2H4zM4 11h16v2H4zM4 15h10v2H4z" fill="currentColor"/>
    <circle cx="18.5" cy="16" r="2.5" fill="currentColor" opacity={active ? 1 : 0.4}/>
  </svg>
);

const IconRules = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7 3h10a2 2 0 0 1 2 2v14l-4-2-4 2-4-2-4 2V5a2 2 0 0 1 2-2h2z" fill="currentColor"/>
  </svg>
);

const IconLeaders = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="7" r="3" fill="currentColor"/>
    <path d="M4 20a8 8 0 0 1 16 0" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
  </svg>
);

const IconChamps = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 3h12v4a6 6 0 0 1-12 0V3z" fill="currentColor"/>
    <path d="M8 21h8v-3H8z" fill="currentColor" opacity=".7"/>
  </svg>
);

// Helper for ?tab=
const getActiveTab = (search) => {
  const sp = new URLSearchParams(search);
  const raw = sp.get('tab');
  if (!raw) return 'Picks';
  const norm = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  return ['Picks','Teams','Trades','Calc','Sheet'].includes(norm) ? norm : 'Picks';
};

const NavBar = () => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const firstSheetLinkRef = useRef(null);
  const loc = useLocation();
  
  // Active on "/", "/draft-board", and anything under "/draft-board/*"
  const onDraftBoard =
    loc.pathname === '/' ||
    loc.pathname.startsWith('/draft-board');

  const activeTab = getActiveTab(loc.search);

  useEffect(() => { setSheetOpen(false); }, [loc.pathname, loc.search]);

  useEffect(() => {
    if (sheetOpen) {
      const t = setTimeout(() => firstSheetLinkRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [sheetOpen]);

  const toggleSheet = () => setSheetOpen(v => !v);

  return (
    <div className="nav-root">
      {/* ===== Mobile Header (always visible on mobile) ===== */}
      <div className="mobile-header" role="banner" aria-label="Mobile Header">
        <div className="mobile-header-inner">
          <div className="mobile-brand-section">
            <div className="mobile-brand-logo">
              <NavLink to="/rules" className="brand-home-link" aria-label="Home (Rules)">
                <img src="./logo.png" alt="Golden Goose" className="mobile-logo-image" />
              </NavLink>
            </div>
            <div className="mobile-brand-text">
              <h1 className="mobile-brand-title">Golden Goose</h1>
              <p className="mobile-brand-subtitle">Fantasy Football League</p>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Modern Desktop Header ===== */}
      <div className="modern-header" role="navigation" aria-label="Primary">
        <div className="header-inner">
          {/* Brand Section */}
          <div className="brand-section">
            <div className="brand-logo">
              <NavLink to="/rules" className="brand-home-link" aria-label="Home (Rules)">
                <img src="./logo.png" alt="Golden Goose" className="logo-image" />
              </NavLink>
            </div>
            <div className="brand-text">
              <h1 className="brand-title">Golden Goose</h1>
              <p className="brand-subtitle">Fantasy Football League</p>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="nav-links">
            <NavLink 
              to="/draft-board" 
              className={({isActive}) => `nav-link ${isActive ? 'nav-link--active' : ''}`}
            >
              <IconDraft active={onDraftBoard} />
              <span>Draft Board</span>
            </NavLink>
            <NavLink 
              to="/rules" 
              className={({isActive}) => `nav-link ${isActive ? 'nav-link--active' : ''}`}
            >
              <IconRules />
              <span>Rules</span>
            </NavLink>
            <NavLink 
              to="/leadership" 
              className={({isActive}) => `nav-link ${isActive ? 'nav-link--active' : ''}`}
            >
              <IconLeaders />
              <span>Leadership</span>
            </NavLink>
            <NavLink 
              to="/past-champions" 
              className={({isActive}) => `nav-link ${isActive ? 'nav-link--active' : ''}`}
            >
              <IconChamps />
              <span>Champions</span>
            </NavLink>
          </div>
        </div>
      </div>

      {/* Desktop Subnav for Draft Board */}
      {onDraftBoard && (
          <div className="subnav" role="navigation" aria-label="Draft Board">
          <div className="subnav-inner">
            {['Picks','Teams','Trades','Calc','Sheet'].map((label) => (
              <NavLink
                key={label}
                to={`/draft-board?tab=${label}`}
                className={'sub-link' + (activeTab === label ? ' sub-link--active' : '')}
              >
                {({
                  Picks: 'Current Draft Board',
                  Teams: 'Picks by Team',
                  Trades: 'Completed Trades',
                  Calc: 'Trade Calculator',
                  Sheet: 'Official Google Sheet',
                })[label]}
              </NavLink>
            ))}
          </div>
        </div>
      )}

      {/* ===== Mobile Bottom Navigation ===== */}
      <div className="mobile-nav" role="navigation" aria-label="App tabs">
        {/* Backdrop for sheet */}
        <div
          className={`sheet-backdrop ${sheetOpen ? 'sheet-backdrop--open' : ''}`}
          onClick={() => setSheetOpen(false)}
        />
        
        {/* Draft submenu sheet */}
        <div className={`sheet ${sheetOpen ? 'sheet--open' : ''}`} aria-hidden={!sheetOpen}>
          <div className="sheet-inner">
            <div className="sheet-title">Draft Resources</div>
            {['Picks','Teams','Trades','Calc','Sheet'].map((label, i) => (
              <NavLink
                key={label}
                to={`/draft-board?tab=${label}`}
                className={'sheet-link' + (activeTab === label ? ' sheet-link--active' : '')}
                ref={i === 0 ? firstSheetLinkRef : undefined}
                onClick={() => setSheetOpen(false)}
              >
                <span>{({
                  Picks: 'Current Draft Board',
                  Teams: 'Picks by Team',
                  Trades: 'Completed Trades',
                  Calc: 'Trade Calculator',
                  Sheet: 'Official Google Sheet',
                })[label]}</span>
                <span aria-hidden="true">›</span>
              </NavLink>
            ))}
          </div>
        </div>

        {/* Mobile Tabs */}
        <div className="mobile-tabs">
          <NavLink
            to="/draft-board"
            className={({ isActive }) =>
              `mobile-tab mobile-tab--draft ${isActive || onDraftBoard ? 'mobile-tab--active' : ''}`
            }
            onClick={(e) => {
              if (onDraftBoard) {
                e.preventDefault();
                toggleSheet();
              }
            }}
            aria-expanded={onDraftBoard ? sheetOpen : undefined}
            aria-controls="draft-sheet"
          >
            <IconDraft active={onDraftBoard} />
            <span className="tab-label">Draft</span>
            
            {/* Dropdown indicator */}
            {onDraftBoard && (
              <svg
                aria-hidden="true"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                className="dropdown-indicator"
              >
                <path
                  d="M6 15l6-6 6 6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </NavLink>

          <NavLink
            to="/rules"
            className={({isActive}) => `mobile-tab ${isActive ? 'mobile-tab--active' : ''}`}
          >
            <IconRules/>
            <span className="tab-label">Rules</span>
          </NavLink>

          <NavLink
            to="/leadership"
            className={({isActive}) => `mobile-tab ${isActive ? 'mobile-tab--active' : ''}`}
          >
            <IconLeaders/>
            <span className="tab-label">Commish</span>
          </NavLink>

          <NavLink
            to="/past-champions"
            className={({isActive}) => `mobile-tab ${isActive ? 'mobile-tab--active' : ''}`}
          >
            <IconChamps/>
            <span className="tab-label">Champs</span>
          </NavLink>
        </div>
      </div>
    </div>
  );
};

export default NavBar;
